import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';

const ManagerPricing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    diesel_price_per_liter: '',
    def_price_per_liter: '',
    diesel_sales_tax: '',
    def_sales_tax: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setError('');
      const response = await api.get('/pricing/pricing/');
      const pricingData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setPricing(pricingData);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setError('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pricingItem = null) => {
    if (pricingItem) {
      setEditingPricing(pricingItem);
      // Convert decimal to percentage for display (e.g., 0.0825 -> 8.25)
      const dieselTaxPercent = pricingItem.diesel_sales_tax ? (parseFloat(pricingItem.diesel_sales_tax) * 100).toFixed(2) : '';
      const defTaxPercent = pricingItem.def_sales_tax ? (parseFloat(pricingItem.def_sales_tax) * 100).toFixed(2) : '';
      setFormData({
        date: pricingItem.date,
        diesel_price_per_liter: pricingItem.diesel_price_per_liter,
        def_price_per_liter: pricingItem.def_price_per_liter,
        diesel_sales_tax: dieselTaxPercent,
        def_sales_tax: defTaxPercent,
      });
    } else {
      setEditingPricing(null);
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        date: today,
        diesel_price_per_liter: '',
        def_price_per_liter: '',
        diesel_sales_tax: '',
        def_sales_tax: '',
      });
    }
    setMessage('');
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPricing(null);
    setFormData({
      date: '',
      diesel_price_per_liter: '',
      def_price_per_liter: '',
      diesel_sales_tax: '',
      def_sales_tax: '',
    });
    setMessage('');
    setError('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      // Convert percentage to decimal (e.g., 8.25% -> 0.0825) and round to 4 decimal places
      const dieselTaxDecimal = formData.diesel_sales_tax 
        ? Math.round((parseFloat(formData.diesel_sales_tax) / 100) * 10000) / 10000 
        : 0;
      const defTaxDecimal = formData.def_sales_tax 
        ? Math.round((parseFloat(formData.def_sales_tax) / 100) * 10000) / 10000 
        : 0;

      const submitData = {
        ...formData,
        diesel_price_per_liter: parseFloat(formData.diesel_price_per_liter),
        def_price_per_liter: parseFloat(formData.def_price_per_liter),
        diesel_sales_tax: parseFloat(dieselTaxDecimal.toFixed(4)),
        def_sales_tax: parseFloat(defTaxDecimal.toFixed(4)),
      };

      if (editingPricing) {
        await api.put(`/pricing/pricing/${editingPricing.id}/`, submitData);
        setMessage('Pricing updated successfully');
      } else {
        await api.post('/pricing/pricing/', submitData);
        setMessage('Pricing created successfully');
      }

      setTimeout(() => {
        handleCloseDialog();
        fetchPricing();
      }, 1000);
    } catch (error) {
      console.error('Error saving pricing:', error);
      const errorMsg = error.response?.data?.error ||
                      error.response?.data?.message ||
                      Object.values(error.response?.data || {}).flat().join(', ') ||
                      'Error saving pricing. Please check your input.';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pricingId) => {
    if (!window.confirm('Are you sure you want to delete this pricing record?')) {
      return;
    }

    try {
      await api.delete(`/pricing/pricing/${pricingId}/`);
      setMessage('Pricing deleted successfully');
      fetchPricing();
    } catch (error) {
      console.error('Error deleting pricing:', error);
      setError('Failed to delete pricing');
    }
  };

  const formatTaxRate = (tax) => {
    if (!tax) return '0%';
    return `${(parseFloat(tax) * 100).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Pricing Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Pricing
          </Button>
        </Box>

        {message && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Diesel Price/L</TableCell>
                <TableCell>Diesel Sales Tax</TableCell>
                <TableCell>DEF Price/L</TableCell>
                <TableCell>DEF Sales Tax</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No pricing records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pricing.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDateOnlyForDisplay(item.date)}</TableCell>
                    <TableCell>${parseFloat(item.diesel_price_per_liter).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatTaxRate(item.diesel_sales_tax)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>${parseFloat(item.def_price_per_liter).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatTaxRate(item.def_sales_tax)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.created_by_name || 'N/A'}</TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            {editingPricing ? 'Edit Pricing' : 'Add New Pricing'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Diesel Price per Liter ($)"
                    name="diesel_price_per_liter"
                    type="number"
                    value={formData.diesel_price_per_liter}
                    onChange={handleChange}
                    required
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Diesel Sales Tax (%)"
                    name="diesel_sales_tax"
                    type="number"
                    value={formData.diesel_sales_tax}
                    onChange={handleChange}
                    helperText="Enter as percentage (e.g., 8.25 for 8.25%) - max 4 decimal places"
                    inputProps={{ step: '0.0001', min: '0', max: '100' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DEF Price per Liter ($)"
                    name="def_price_per_liter"
                    type="number"
                    value={formData.def_price_per_liter}
                    onChange={handleChange}
                    required
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DEF Sales Tax (%)"
                    name="def_sales_tax"
                    type="number"
                    value={formData.def_sales_tax}
                    onChange={handleChange}
                    helperText="Enter as percentage (e.g., 8.25 for 8.25%) - max 4 decimal places"
                    inputProps={{ step: '0.0001', min: '0', max: '100' }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : editingPricing ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerPricing;

